/**
 * SONAUTO — customer-facing i18n key catalogue.
 *
 * SCOPE: this file lists ONLY strings a paying guest can see (the QR ordering
 * flow, order status, service requests, payment, errors). Staff-facing UI —
 * Admin App, POS App, Staff App, KDS — is Vietnamese only and must never get
 * keys here; adding one is the signal that someone is about to translate an
 * internal screen by accident.
 *
 * This array is the CONTRACT the translation-key validation test enforces:
 * every key must exist, published, for every locale a branch has enabled
 * (see `validateBundle` in ./bundle.ts). Keys map 1:1 onto
 * `customer_ui_translations.translation_key` with `namespace = 'customer'`.
 *
 * NAMING: `customer.<area>.<thing>` — lowercase, dot separated, snake_case
 * leaves. Never encode a language, a branch or a tenant in a key.
 *
 * Placeholders use `{name}` and are interpolated by `createTranslator`. The
 * placeholder set is part of the key's contract: a translation that drops or
 * invents one is a review finding, not a formatting detail.
 */

export const CUSTOMER_UI_KEYS = [
  // --- app shell / navigation -------------------------------------------
  'customer.app.name',
  'customer.app.welcome',
  'customer.app.loading',
  'customer.nav.menu',
  'customer.nav.cart',
  'customer.nav.orders',
  'customer.nav.help',
  'customer.nav.back',
  'customer.nav.close',
  'customer.nav.done',

  // --- table / session ---------------------------------------------------
  'customer.table.number', // {number}
  'customer.table.area',
  'customer.table.guests', // {count}
  'customer.session.welcome', // {branch}
  'customer.session.expires_in', // {minutes}
  'customer.session.closed',

  // --- menu --------------------------------------------------------------
  'customer.menu.title',
  'customer.menu.search_placeholder',
  'customer.menu.search_no_result', // {query}
  'customer.menu.empty',
  'customer.menu.recommended',
  'customer.menu.best_seller',
  'customer.menu.new_item',
  'customer.menu.promotion',
  'customer.menu.sold_out',
  'customer.menu.category_all',
  'customer.menu.category_empty',
  'customer.menu.view_item',

  // --- item detail -------------------------------------------------------
  'customer.item.description',
  'customer.item.ingredients',
  'customer.item.allergens', // legally sensitive: HUMAN review required
  'customer.item.allergen_warning',
  'customer.item.calories', // {value}
  'customer.item.preparation_time', // {minutes}
  'customer.item.serving_note',
  'customer.item.unavailable',
  'customer.item.choose_options',
  'customer.item.required_option',
  'customer.item.optional_option',
  'customer.item.select_min', // {min}
  'customer.item.select_max', // {max}

  // --- option groups (size / spice / ice / sugar / topping) --------------
  'customer.option.size',
  'customer.option.size_small',
  'customer.option.size_medium',
  'customer.option.size_large',
  'customer.option.spice_level',
  'customer.option.spice_none',
  'customer.option.spice_mild',
  'customer.option.spice_medium',
  'customer.option.spice_hot',
  'customer.option.spice_extra_hot',
  'customer.option.ice',
  'customer.option.ice_none',
  'customer.option.ice_less',
  'customer.option.ice_normal',
  'customer.option.ice_extra',
  'customer.option.sugar',
  'customer.option.sugar_none',
  'customer.option.sugar_less',
  'customer.option.sugar_normal',
  'customer.option.sugar_extra',
  'customer.option.topping',
  'customer.option.topping_add', // {name}
  'customer.option.extra_charge', // {amount}
  'customer.option.free',

  // --- quantity & note ---------------------------------------------------
  'customer.quantity.label',
  'customer.quantity.increase',
  'customer.quantity.decrease',
  'customer.quantity.max_reached', // {max}
  'customer.note.label',
  'customer.note.placeholder',
  'customer.note.too_long', // {max}

  // --- cart --------------------------------------------------------------
  'customer.cart.title',
  'customer.cart.empty',
  'customer.cart.empty_hint',
  'customer.cart.add_item',
  'customer.cart.added', // {name}
  'customer.cart.remove_item',
  'customer.cart.removed', // {name}
  'customer.cart.edit_item',
  'customer.cart.item_count', // {count}
  'customer.cart.clear',
  'customer.cart.clear_confirm',
  'customer.cart.continue_ordering',

  // --- totals ------------------------------------------------------------
  'customer.total.subtotal',
  'customer.total.discount',
  'customer.total.promotion_applied', // {name}
  'customer.total.service_charge', // {percent}
  'customer.total.tax', // {percent}
  'customer.total.delivery_fee',
  'customer.total.rounding',
  'customer.total.grand_total',
  'customer.total.paid',
  'customer.total.remaining',

  // --- order submission --------------------------------------------------
  'customer.order.title',
  'customer.order.submit',
  'customer.order.submitting',
  'customer.order.confirm_title',
  'customer.order.confirm_body',
  'customer.order.confirmed',
  'customer.order.number', // {number}
  'customer.order.placed_at', // {time}
  'customer.order.cancel',
  'customer.order.cancel_confirm',
  'customer.order.cancelled',
  'customer.order.add_more',

  // --- order status timeline --------------------------------------------
  'customer.order.status_title',
  'customer.order.received',
  'customer.order.preparing',
  'customer.order.ready',
  'customer.order.serving',
  'customer.order.completed',
  'customer.order.estimated_time', // {minutes}
  'customer.order.estimated_time_unknown',
  'customer.order.delayed', // {minutes}
  'customer.order.item_status_pending',
  'customer.order.item_status_preparing',
  'customer.order.item_status_served',
  'customer.order.history_title',
  'customer.order.history_empty',

  // --- service requests ---------------------------------------------------
  'customer.call_staff',
  'customer.call_staff_sent',
  'customer.request_water',
  'customer.request_utensils',
  'customer.request_clean_table',
  'customer.request_support',
  'customer.request_sent',
  'customer.request_pending', // {minutes}
  'customer.request_acknowledged',
  'customer.report_missing_item',
  'customer.report_wrong_item',

  // --- payment ------------------------------------------------------------
  'customer.request_payment',
  'customer.payment.title',
  'customer.payment.method_cash',
  'customer.payment.method_card',
  'customer.payment.method_transfer',
  'customer.payment.method_ewallet',
  'customer.payment.qr_hint',
  'customer.payment.split_bill',
  'customer.payment.staff_coming',
  'customer.payment.received',
  'customer.payment.receipt',
  'customer.payment.receipt_email',

  // --- closing ------------------------------------------------------------
  'customer.thank_you',
  'customer.thank_you_body',
  'customer.rate_us',
  'customer.rate_us_hint',
  'customer.rate_us_submit',
  'customer.rate_us_thanks',
  'customer.feedback.placeholder',

  // --- language switch ----------------------------------------------------
  'customer.language.switch',
  'customer.language.current', // {language}
  'customer.language.choose',
  'customer.language.machine_translated_notice',

  // --- connectivity / retry ------------------------------------------------
  'customer.offline.banner',
  'customer.offline.queued',
  'customer.offline.reconnected',
  'customer.retry',
  'customer.refresh',

  // --- errors ---------------------------------------------------------------
  'customer.error.session_expired',
  'customer.error.item_unavailable',
  'customer.error.qr_invalid',
  'customer.error.qr_expired',
  'customer.error.table_occupied',
  'customer.error.order_failed',
  'customer.error.payment_failed',
  'customer.error.network',
  'customer.error.rate_limited',
  'customer.error.generic',
  'customer.error.try_again',
  'customer.error.contact_staff',

  // --- generic actions -------------------------------------------------------
  'customer.action.confirm',
  'customer.action.cancel',
  'customer.action.ok',
  'customer.action.yes',
  'customer.action.no',
  'customer.action.save',
] as const;

export type CustomerUiKey = (typeof CUSTOMER_UI_KEYS)[number];

const KEY_SET: ReadonlySet<string> = new Set<string>(CUSTOMER_UI_KEYS);

export function isCustomerUiKey(key: string): key is CustomerUiKey {
  return KEY_SET.has(key);
}

/** Default namespace for these keys (`customer_ui_translations.namespace`). */
export const CUSTOMER_NAMESPACE = 'customer';

/**
 * Message codes from `customer_message_translations.message_code`. These are
 * server-emitted, template-driven messages (in-app, SMS, print) rather than
 * static UI labels, so they live in their own table — but they are equally
 * customer facing and equally required per locale.
 */
export const CUSTOMER_MESSAGE_CODES = [
  'CALL_STAFF',
  'REQUEST_WATER',
  'REQUEST_UTENSILS',
  'REQUEST_CLEAN_TABLE',
  'REQUEST_PAYMENT',
  'ASK_ORDER_STATUS',
  'REPORT_MISSING_ITEM',
  'REPORT_WRONG_ITEM',
  'REQUEST_SUPPORT',
  'ORDER_RECEIVED',
  'ORDER_PREPARING',
  'ORDER_COMPLETED',
  'ITEM_UNAVAILABLE',
  'PAYMENT_RECEIVED',
  'SESSION_EXPIRED',
  'THANK_YOU',
] as const;

export type CustomerMessageCode = (typeof CUSTOMER_MESSAGE_CODES)[number];

/**
 * Keys whose translation is legally or safety sensitive. The pipeline must
 * force NEEDS_REVIEW for these even under `AI_AUTO_PUBLISH` — a wrong allergen
 * string is a hospitalisation, not a typo.
 */
export const REVIEW_REQUIRED_KEYS: readonly CustomerUiKey[] = [
  'customer.item.allergens',
  'customer.item.allergen_warning',
  'customer.item.ingredients',
  'customer.total.tax',
  'customer.total.service_charge',
];

/** Extract the `{placeholder}` names declared in a template string. */
export function extractPlaceholders(template: string): readonly string[] {
  const out: string[] = [];
  const re = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;
  let m: RegExpExecArray | null = re.exec(template);
  while (m !== null) {
    const name = m[1];
    if (name !== undefined && !out.includes(name)) out.push(name);
    m = re.exec(template);
  }
  return out;
}
